import { LanguagePipe } from '../pipe/language.pipe';

/**
 * The two server-side pipeline stages a resource id badge can report on.
 *
 * `PREPARE`     — `Resource.prepared`, the upload being turned into a
 *                 usable artefact. Exists for Drafts AND Books.
 * `STANDARDIZE` — `Cover.standardized` / `Body.standardized`, the second
 *                 stage that runs on the prepared artefact. **Book side
 *                 only** — `DraftCover` and `DraftBody` have no such
 *                 field, which is the whole reason the two cases cannot
 *                 be rendered identically.
 */
export type ResourceStage = 'PREPARE' | 'STANDARDIZE';

/**
 * Shared status vocabulary for the resource-id badge.
 *
 * WHY THIS EXISTS
 * ---------------
 * `getResourceStatusColor` was copy-pasted into five components
 * (project-book, project-book-thumbnail, project-draft,
 * project-draft-thumbnail, classic-project-thumbnail). Every one of them
 * had its own copy of the same switch statement, so the palette could
 * drift between the Draft view and the Book view without anything
 * failing. Adding a second dimension (standardized) to five hand-copied
 * switches would have guaranteed that drift. One definition here, thin
 * delegating wrappers there.
 *
 * THE THREE VISUAL CHANNELS
 * -------------------------
 * The badge encodes three independent things, and each has exactly one
 * channel so they can never be confused with one another:
 *
 *   background fill  -> `Resource.prepared`      (Draft + Book, identical)
 *   bottom stripe    -> `*.standardized`         (Book only)
 *   outline + cursor -> "clicking this does something"
 *
 * Text colour is deliberately NOT used: `classic-project-thumbnail`
 * already spends it on `.diff` / `.diff-primary`, which mark where a
 * Draft differs from the Primary Book. Taking it for status would have
 * silently broken that comparison.
 *
 * WHY THE STRIPE REUSES THE FILL PALETTE
 * --------------------------------------
 * The stripe is only ever drawn when `prepared === 'COMPLETE'`, i.e. on
 * a green fill. Because it reuses the same palette:
 *
 *   standardized COMPLETE -> green stripe on green fill -> INVISIBLE
 *   anything else         -> a contrasting band that stands out
 *
 * So "solid green, no stripe" reads as *completely finished*, and the
 * badge stays as quiet as it is today in the common case. The operator
 * only sees extra ink when there is genuinely something outstanding,
 * and the colour already means the same thing they learned from the
 * fill. No new vocabulary.
 */
export class ResourceStatus {

  /**
   * One palette, used for BOTH the fill and the stripe.
   *
   * Keeping them identical is what makes the "invisible when done"
   * property work, and means an operator never has to learn a second
   * colour language for the second stage.
   */
  private static readonly PALETTE: { [status: string]: string } = {
    COMPLETE:    '#c8e6c9',
    ERROR:       '#ffcdd2',
    INITIAL:     '#e0e0e0',
    WAITING:     '#ffe0b2',
    IN_PROGRESS: '#fff9c4',
  };

  /**
   * Height of the standardized stripe, in px. Kept small because the
   * thumbnail badges render at `font-size: 9px` — anything thicker
   * reads as a second background rather than an accent. The matching
   * `background-size` lives in `src/styles.css`.
   */
  public static readonly STRIPE_HEIGHT_PX = 3;

  private static readonly NO_RESOURCE = 'transparent';
  /** A resource id that points at a row we cannot find. Deliberately loud. */
  private static readonly DANGLING = 'plum';

  // ── Fill: Resource.prepared ───────────────────────────────────────

  /**
   * Background colour for the badge.
   *
   * `resource` is the resolved `Resource` row, or null if it has not
   * arrived yet / does not exist. `hasId` distinguishes "no resource id
   * at all" (transparent — nothing to report) from "id set but the row
   * is missing" (plum — a dangling reference worth noticing).
   */
  public static fill(hasId: boolean, resource: { prepared?: string } | null): string {
    if (!hasId) { return ResourceStatus.NO_RESOURCE; }
    if (!resource) { return ResourceStatus.DANGLING; }
    return ResourceStatus.PALETTE[resource.prepared] || ResourceStatus.PALETTE.INITIAL;
  }

  // ── Stripe: *.standardized ────────────────────────────────────────

  /**
   * `background-image` value drawing the standardized stripe, or
   * `'none'`.
   *
   * Returns a flat two-stop gradient; the geometry (4px tall, pinned to
   * the bottom, no-repeat) lives in CSS so this only has to decide a
   * colour. That split means the stripe cannot accidentally cover the
   * fill even if a bad status string arrives.
   *
   * Drawn ONLY when preparation is COMPLETE. Before that the
   * standardized value is meaningless — standardization reads the
   * prepared artefact, so a Draft-stage `standardized` of INITIAL says
   * nothing about whether work is owed. Showing a grey stripe there
   * would report a problem that does not exist yet.
   *
   * `standardized` is undefined for Drafts (no such field), which falls
   * through to 'none' — the Draft badge is exactly what it is today.
   */
  public static stripe(resource: { prepared?: string } | null, standardized?: string): string {
    if (!resource || resource.prepared !== 'COMPLETE') { return 'none'; }
    if (!standardized) { return 'none'; }
    const colour = ResourceStatus.PALETTE[standardized];
    if (!colour) { return 'none'; }
    return 'linear-gradient(' + colour + ', ' + colour + ')';
  }

  // ── Affordance: what does clicking do? ────────────────────────────

  /**
   * Which stage, if any, the operator can start from this badge.
   *
   * Only ever advances INITIAL -> WAITING. Everything else is either
   * already queued (WAITING / IN_PROGRESS), already done (COMPLETE), or
   * broken in a way a re-request will not fix (ERROR — the upload
   * itself has to be corrected first).
   *
   * The ordering matters: preparation is offered first, and
   * standardization only once preparation is COMPLETE. Requesting
   * standardization against an unprepared resource queues work behind a
   * file that does not exist and lands the row in ERROR for a reason
   * that has nothing to do with the PDF. That exact bug is documented
   * in `ProjectBookComponent.canStandardize`.
   */
  public static action(resource: { prepared?: string } | null, standardized?: string): ResourceStage | null {
    if (!resource) { return null; }
    if (resource.prepared === 'INITIAL') { return 'PREPARE'; }
    if (resource.prepared === 'COMPLETE' && standardized === 'INITIAL') { return 'STANDARDIZE'; }
    return null;
  }

  public static isActionable(resource: { prepared?: string } | null, standardized?: string): boolean {
    return ResourceStatus.action(resource, standardized) !== null;
  }

  // ── Text ──────────────────────────────────────────────────────────

  /** Translated label for a pipeline status value. */
  public static statusLabel(status: string): string {
    switch (status) {
      case 'INITIAL':     return LanguagePipe.get('Not Started', 'compressSpaces');
      // Registered in app.component.html as `@@WAITING` (value "Waiting").
      // Asking for 'Waiting' misses the runtime map — the key is the span
      // id minus its prefix, and ids are case-sensitive — so it warned
      // 'No Language String for: Waiting' and rendered English in every
      // locale. The other four cases match their spans exactly.
      case 'WAITING':     return LanguagePipe.get('WAITING', 'compressSpaces');
      case 'IN_PROGRESS': return LanguagePipe.get('In Progress', 'compressSpaces');
      case 'COMPLETE':    return LanguagePipe.get('Complete', 'compressSpaces');
      case 'ERROR':       return LanguagePipe.get('Error', 'compressSpaces');
      default:            return LanguagePipe.get('Unknown', 'compressSpaces');
    }
  }

  /**
   * Tooltip carrying everything the colours encode, in words.
   *
   * The badge is small and colour-only; this is what makes it
   * accessible and what a colour-blind operator actually reads. It is
   * also the only place the standardized value appears as text on the
   * thumbnails, which is why it is assembled here rather than being
   * left to each caller to format differently.
   */
  public static hoverText(
    label: string,
    hasId: boolean,
    resource: { prepared?: string } | null,
    standardized?: string
  ): string {
    const parts: string[] = [];
    let prepared: string;
    if (!hasId) {
      prepared = LanguagePipe.get('Not Set', 'compressSpaces');
    } else if (!resource) {
      prepared = LanguagePipe.get('Not Found', 'compressSpaces');
    } else {
      prepared = ResourceStatus.statusLabel(resource.prepared);
    }
    parts.push(LanguagePipe.get(label, 'compressSpaces') + ': ' + prepared);

    // Only meaningful once preparation finished; see stripe().
    if (standardized && resource && resource.prepared === 'COMPLETE') {
      parts.push(LanguagePipe.get('Standardized', 'compressSpaces') + ': '
        + ResourceStatus.statusLabel(standardized));
    }

    const stage = ResourceStatus.action(resource, standardized);
    if (stage === 'PREPARE') {
      parts.push(LanguagePipe.get('Click to request preparation', 'compressSpaces'));
    } else if (stage === 'STANDARDIZE') {
      parts.push(LanguagePipe.get('Click to request standardization', 'compressSpaces'));
    }
    return parts.join(' \u00B7 ');
  }

  /** `----` when there is no resource id, otherwise the id. */
  public static displayString(resource_ID: number): string {
    return resource_ID ? resource_ID.toString() : '----';
  }
}



